import React, { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import NavBar from "../../components/common/NavBar";
import {
    GOOGLE_PLACE_COUNTRY_OPTIONS,
    GOOGLE_PLACE_STATE_OPTIONS_BY_COUNTRY,
    SPORTS,
} from "../../constants/vars";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";

const AUDIENCE_ESTIMATES = gql`
    query AdvertisingAudienceEstimates(
        $sportKey: String
        $organizationId: ID
        $city: String
        $state: String
        $country: String
        $leagueLimit: Int
        $activePeriodDays: Int
    ) {
        advertisingAudienceEstimates(
            sportKey: $sportKey
            organizationId: $organizationId
            city: $city
            state: $state
            country: $country
            leagueLimit: $leagueLimit
            activePeriodDays: $activePeriodDays
        ) {
            generatedAt
            summary {
                totalUsers
                activeUsers
                newUsers
                active7Days
                active30Days
                active90Days
                active180Days
                active365Days
            }
            sports {
                sportKey
                sportName
                totalUsers
                activeUsers
                active7Days
                active30Days
                active90Days
                active180Days
                active365Days
            }
            leagues {
                organizationId
                organizationName
                sportKey
                sportName
                totalUsers
                activeUsers
                newUsers
                active7Days
                active30Days
                active90Days
                active180Days
                active365Days
            }
            newUsersTrend {
                date
                newUsers
            }
        }
    }
`;

const ACTIVE_PERIOD_OPTIONS = [
    { value: "active7Days", days: 7, labelKey: "last7Days" },
    { value: "active30Days", days: 30, labelKey: "last30Days" },
    { value: "active90Days", days: 90, labelKey: "last3Months" },
    { value: "active180Days", days: 180, labelKey: "last6Months" },
    { value: "active365Days", days: 365, labelKey: "last1Year" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

function SummaryCard({ icon, label, value, hint, className = "col-12 col-md-4" }) {
    return (
        <div className={className}>
            <div className="card border-0 shadow-sm audience-summary-card">
                <div className="card-body d-flex align-items-center">
                    <div className="audience-summary-icon">
                        <i className={icon} />
                    </div>
                    <div>
                        <div className="text-muted text-xs text-uppercase font-weight-bold">{label}</div>
                        <div className="h3 mb-0 font-weight-bold">{value}</div>
                        <div className="text-muted text-sm">{hint}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TableCard({ title, emptyText, children }) {
    return (
        <div className="card border-0 shadow-sm audience-table-card">
            <div className="card-header bg-white border-0">
                <h5 className="mb-0 font-weight-bold">{title}</h5>
            </div>
            <div className="card-body p-0">
                {children || (
                    <div className="text-center text-muted py-5">
                        {emptyText}
                    </div>
                )}
            </div>
        </div>
    );
}

function AudienceHeaderSection({
                                   t,
                                   isStaff,
                                   sportKey,
                                   setSportKey,
                                   organizationId,
                                   setOrganizationId,
                                   activePeriodKey,
                                   setActivePeriodKey,
                                   cityFilter,
                                   setCityFilter,
                                   stateFilter,
                                   setStateFilter,
                                   countryFilter,
                                   setCountryFilter,
                                   activePeriodOptions,
                                   sportOptions,
                                   leagueOptions,
                                   stateOptions,
                                   hasAudienceFilters,
                                   resetFilters,
                               }) {
    return (
        <div className="card border-0 shadow-sm audience-header-card">
            <div className="card-body">
                <div className="row align-items-end">
                    <div className="col-12 col-lg-12 mb-3">
                        <h4 className="mb-1 font-weight-bold">{t("audience")}</h4>
                        <p className="text-muted mb-lg-0">
                            {isStaff ? t("staffDescription") : t("scopedDescription")}
                        </p>
                    </div>

                    <div className="col-12 col-lg-12 mt-3 mt-lg-0">
                        <div className="audience-filter-panel">
                            <div className="audience-filter-actions">
                                <button type="button" onClick={resetFilters}>
                                    <i className="fas fa-undo-alt" />
                                    {t("reset")}
                                </button>
                            </div>

                            <div className="audience-filter-row">
                                <div className="audience-filter-field">
                                    <label htmlFor="audience-sport">
                                        <i className="fas fa-running" />
                                        {t("sport")}
                                    </label>
                                    <select
                                        id="audience-sport"
                                        value={sportKey}
                                        onChange={(event) => {
                                            setSportKey(event.target.value);
                                            setOrganizationId("");
                                        }}
                                    >
                                        <option value="">{t("allSports")}</option>
                                        {sportOptions.map((sport) => (
                                            <option key={sport.value} value={sport.value}>
                                                {sport.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="audience-filter-field">
                                    <label htmlFor="audience-league">
                                        <i className="fas fa-shield-alt" />
                                        {t("league")}
                                    </label>
                                    <select
                                        id="audience-league"
                                        value={organizationId}
                                        onChange={(event) => setOrganizationId(event.target.value)}
                                    >
                                        <option value="">{t("allVisibleLeagues", {}, "All visible leagues")}</option>
                                        {leagueOptions.map((league) => (
                                            <option key={league.value} value={league.value}>
                                                {league.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="audience-filter-field">
                                    <label htmlFor="audience-active-period">
                                        <i className="fas fa-calendar-alt" />
                                        {t("activePeriod")}
                                    </label>
                                    <select
                                        id="audience-active-period"
                                        value={activePeriodKey}
                                        onChange={(event) => setActivePeriodKey(event.target.value)}
                                    >
                                        {activePeriodOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="audience-filter-row">
                                <div className="audience-filter-field">
                                    <label htmlFor="audience-country">
                                        <i className="fas fa-globe-americas" />
                                        {t("country")}
                                    </label>
                                    <select
                                        id="audience-country"
                                        value={countryFilter}
                                        onChange={(event) => {
                                            setCountryFilter(event.target.value);
                                            setStateFilter("");
                                        }}
                                    >
                                        <option value="">{t("countryPlaceholder")}</option>
                                        {GOOGLE_PLACE_COUNTRY_OPTIONS.map((country) => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="audience-filter-field">
                                    <label htmlFor="audience-state">
                                        <i className="fas fa-map" />
                                        {t("state")}
                                    </label>
                                    <select
                                        id="audience-state"
                                        value={stateFilter}
                                        onChange={(event) => setStateFilter(event.target.value)}
                                        disabled={!countryFilter}
                                    >
                                        <option value="">
                                            {countryFilter ? t("statePlaceholder") : t("selectCountryFirst")}
                                        </option>
                                        {stateOptions.map((state) => (
                                            <option key={state.value} value={state.value}>
                                                {state.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="audience-filter-field">
                                    <label htmlFor="audience-city">
                                        <i className="fas fa-map-marker-alt" />
                                        {t("city")}
                                    </label>
                                    <input
                                        id="audience-city"
                                        type="text"
                                        value={cityFilter}
                                        onChange={(event) => setCityFilter(event.target.value)}
                                        placeholder={t("cityPlaceholder")}
                                    />
                                </div>
                            </div>
                        </div>

                        {isStaff && !hasAudienceFilters ? (
                            <div className="alert alert-info mt-3 mb-0 audience-staff-notice">
                                <i className="fas fa-user-shield mr-2" />
                                {t("staffAllUsersNotice")}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AudienceSummarySection({
                                    t,
                                    summary,
                                    selectedActivePeriod,
                                    isStaff,
                                    hasAudienceFilters,
                                    getActivePeriodValue,
                                }) {
    return (
        <div className="row">
            <SummaryCard
                icon="fas fa-users"
                label={t("reachableUsers", {}, "Reachable users")}
                value={formatNumber(summary.totalUsers)}
                hint={
                    isStaff && !hasAudienceFilters
                        ? t("staffTotalUsersHint")
                        : t("reachableUsersHint")
                }
                className="col-12 col-md-4"
            />
            <SummaryCard
                icon="fas fa-calendar-alt"
                label={t("activeUsers")}
                value={formatNumber(getActivePeriodValue(summary))}
                hint={selectedActivePeriod?.label}
                className="col-12 col-md-4"
            />
            <SummaryCard
                icon="fas fa-user-plus"
                label={t("newUsers")}
                value={formatNumber(summary.newUsers)}
                hint={selectedActivePeriod?.label}
                className="col-12 col-md-4"
            />
        </div>
    );
}

function NewUsersTrendSection({ t, newUsersTrend }) {
    return (
        <div className="col-12 col-xl-12">
            <TableCard title={t("newUsersOverTime")} emptyText={t("noAudienceData")}>
                {newUsersTrend.length > 0 && (
                    <div className="audience-chart">
                        <ResponsiveContainer>
                            <LineChart data={newUsersTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Line
                                    type="monotone"
                                    dataKey="newUsers"
                                    name={t("newUsers")}
                                    stroke="#16a34a"
                                    strokeWidth={3}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </TableCard>
        </div>
    );
}

function AudienceBySportSection({ t, audienceBySport, selectedActivePeriod }) {
    return (
        <div className="col-12 col-xl-12">
            <TableCard title={t("audienceBySport")} emptyText={t("noAudienceData")}>
                {audienceBySport.length > 0 && (
                    <div className="audience-chart">
                        <ResponsiveContainer>
                            <BarChart data={audienceBySport} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="sportName" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar
                                    dataKey="activeUsers"
                                    name={selectedActivePeriod?.label}
                                    fill="#2563eb"
                                    radius={[8, 8, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </TableCard>
        </div>
    );
}

function VisibleLeagueAudienceSection({ t, leagues, getActivePeriodValue }) {
    return (
        <div className="col-12 col-xl-12">
            <TableCard title={t("visibleLeagueAudience", {}, "Visible League Audience")} emptyText={t("noLeagueData")}>
                {leagues.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                            <tr>
                                <th>{t("league")}</th>
                                <th>{t("sport")}</th>
                                <th className="text-right">{t("totalUsers")}</th>
                                <th className="text-right">{t("activeUsers")}</th>
                                <th className="text-right">{t("newUsers")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {leagues.map((league) => (
                                <tr key={`${league.organizationId}-${league.sportKey || "sport"}`}>
                                    <td className="font-weight-bold">{league.organizationName || "—"}</td>
                                    <td>{league.sportName || "—"}</td>
                                    <td className="text-right">{formatNumber(league.totalUsers)}</td>
                                    <td className="text-right">{formatNumber(getActivePeriodValue(league))}</td>
                                    <td className="text-right">{formatNumber(league.newUsers)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </TableCard>
        </div>
    );
}

function Audience() {
    const { t: originalT } = useTranslation();
    const t = (key, params, fallback) => originalT(`advertisingAudience.${key}`, params, fallback);
    const { authData } = useActiveProfile();

    const [sportKey, setSportKey] = useState("");
    const [organizationId, setOrganizationId] = useState("");
    const [cityFilter, setCityFilter] = useState("");
    const [debouncedCityFilter, setDebouncedCityFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [activePeriodKey, setActivePeriodKey] = useState("active30Days");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCityFilter(cityFilter);
        }, 500);

        return () => clearTimeout(timer);
    }, [cityFilter]);

    const isStaff = Boolean(authData?.user?.isStaff);

    const cityFilterValue = debouncedCityFilter.trim();
    const stateFilterValue = stateFilter.trim();
    const countryFilterValue = countryFilter.trim();

    const hasAudienceFilters = Boolean(
        sportKey || organizationId || cityFilterValue || stateFilterValue || countryFilterValue
    );

    const activePeriodOptions = useMemo(
        () =>
            ACTIVE_PERIOD_OPTIONS.map((option) => ({
                ...option,
                label: originalT(`advertisingAudience.${option.labelKey}`),
            })),
        [originalT]
    );

    const selectedActivePeriod =
        activePeriodOptions.find((option) => option.value === activePeriodKey) || activePeriodOptions[1];

    const stateOptions = GOOGLE_PLACE_STATE_OPTIONS_BY_COUNTRY[countryFilter] || [];

    const navBarElem = {
        pageTitle: t("advertising"),
        links: [
            { pageTitle: t("advertising"), navLink: null, active: false },
            { pageTitle: t("audience"), navLink: `/advertise/audience`, active: true },
        ],
    };

    const { data, loading, error, refetch } = useQuery(AUDIENCE_ESTIMATES, {
        variables: {
            sportKey: sportKey || null,
            organizationId: organizationId || null,
            city: cityFilterValue || null,
            state: stateFilterValue || null,
            country: countryFilterValue || null,
            leagueLimit: 25,
            activePeriodDays: selectedActivePeriod?.days || 30,
        },
        fetchPolicy: "network-only",
    });

    const estimates = data?.advertisingAudienceEstimates;
    const summary = estimates?.summary || {};
    const sports = useMemo(() => estimates?.sports || [], [estimates]);
    const leagues = useMemo(() => estimates?.leagues || [], [estimates]);
    const newUsersTrend = useMemo(() => estimates?.newUsersTrend || [], [estimates]);

    const getActivePeriodValue = useCallback(
        (item) => item?.activeUsers ?? item?.[activePeriodKey] ?? item?.active30Days ?? 0,
        [activePeriodKey]
    );

    const sportOptions = useMemo(
        () =>
            SPORTS.map((sport) => ({
                value: sport.key,
                label: originalT(`signUp.form.sports.${sport.key}`, {}, sport.value),
            })).sort((a, b) => a.label.localeCompare(b.label)),
        [originalT]
    );

    const audienceBySport = useMemo(() => {
        const estimatesBySport = new Map(
            sports
                .filter((sport) => sport.sportKey)
                .map((sport) => [sport.sportKey, sport])
        );

        return SPORTS.map((sport) => {
            const estimate = estimatesBySport.get(sport.key) || {};

            return {
                sportKey: sport.key,
                sportName: originalT(`signUp.form.sports.${sport.key}`, {}, sport.value),
                totalUsers: estimate.totalUsers || 0,
                active7Days: estimate.active7Days || 0,
                active30Days: estimate.active30Days || 0,
                active90Days: estimate.active90Days || 0,
                active180Days: estimate.active180Days || 0,
                active365Days: estimate.active365Days || 0,
                activeUsers: getActivePeriodValue(estimate),
            };
        });
    }, [getActivePeriodValue, originalT, sports]);

    const leagueOptions = useMemo(() => {
        return leagues
            .map((league) => ({
                value: league.organizationId,
                label: league.organizationName,
            }))
            .filter((league) => league.value)
            .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    }, [leagues]);

    const resetFilters = () => {
        setSportKey("");
        setOrganizationId("");
        setCityFilter("");
        setDebouncedCityFilter("");
        setStateFilter("");
        setCountryFilter("");
        setActivePeriodKey("active30Days");
    };

    return (
        <div className="content-wrapper" id="kbola-advertising-audience-page">
            <NavBar navBarElem={navBarElem} />

            <section className="content">
                <div className="container-fluid">
                    <AudienceHeaderSection
                        t={t}
                        isStaff={isStaff}
                        sportKey={sportKey}
                        setSportKey={setSportKey}
                        organizationId={organizationId}
                        setOrganizationId={setOrganizationId}
                        activePeriodKey={activePeriodKey}
                        setActivePeriodKey={setActivePeriodKey}
                        cityFilter={cityFilter}
                        setCityFilter={setCityFilter}
                        stateFilter={stateFilter}
                        setStateFilter={setStateFilter}
                        countryFilter={countryFilter}
                        setCountryFilter={setCountryFilter}
                        activePeriodOptions={activePeriodOptions}
                        sportOptions={sportOptions}
                        leagueOptions={leagueOptions}
                        stateOptions={stateOptions}
                        hasAudienceFilters={hasAudienceFilters}
                        resetFilters={resetFilters}
                    />

                    {error && (
                        <div className="alert alert-danger">
                            {getLocalizedGraphQLError(error, t, "loadError")}
                        </div>
                    )}

                    {loading && !estimates ? (
                        <div className="text-center text-muted py-5">{t("loading")}</div>
                    ) : (
                        <>
                            <AudienceSummarySection
                                t={t}
                                summary={summary}
                                selectedActivePeriod={selectedActivePeriod}
                                isStaff={isStaff}
                                hasAudienceFilters={hasAudienceFilters}
                                getActivePeriodValue={getActivePeriodValue}
                            />

                            <div className="row">
                                <NewUsersTrendSection
                                    t={t}
                                    newUsersTrend={newUsersTrend}
                                />

                                <AudienceBySportSection
                                    t={t}
                                    audienceBySport={audienceBySport}
                                    selectedActivePeriod={selectedActivePeriod}
                                />

                                <VisibleLeagueAudienceSection
                                    t={t}
                                    leagues={leagues}
                                    getActivePeriodValue={getActivePeriodValue}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>

            <style>{`
                #kbola-advertising-audience-page .audience-header-card,
                #kbola-advertising-audience-page .audience-summary-card,
                #kbola-advertising-audience-page .audience-table-card {
                    border-radius: 8px;
                }

                #kbola-advertising-audience-page .audience-filter-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                #kbola-advertising-audience-page .audience-filter-actions {
                    display: flex;
                    justify-content: flex-end;
                }

                #kbola-advertising-audience-page .audience-filter-row {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(160px, 1fr));
                    gap: 12px;
                    align-items: end;
                }

                #kbola-advertising-audience-page .audience-filter-field {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    min-width: 0;
                }

                #kbola-advertising-audience-page .audience-filter-panel label {
                    margin: 0;
                    color: #475569;
                    font-size: 12px;
                    font-weight: 700;
                    text-transform: uppercase;
                }

                #kbola-advertising-audience-page .audience-filter-panel label i {
                    color: #2563eb;
                    margin-right: 6px;
                }

                #kbola-advertising-audience-page .audience-filter-panel select,
                #kbola-advertising-audience-page .audience-filter-panel input {
                    height: 42px;
                    border: 1px solid #d6dde8;
                    border-radius: 8px;
                    padding: 0 12px;
                    background: #fff;
                    min-width: 0;
                }

                #kbola-advertising-audience-page .audience-filter-panel button {
                    height: 42px;
                    border: 0;
                    border-radius: 8px;
                    padding: 0 14px;
                    background: #eff6ff;
                    color: #1d4ed8;
                    font-weight: 700;
                    white-space: nowrap;
                }

                #kbola-advertising-audience-page .audience-filter-panel button i {
                    margin-right: 6px;
                }

                #kbola-advertising-audience-page .audience-summary-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 8px;
                    background: #eff6ff;
                    color: #2563eb;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 14px;
                    flex: 0 0 auto;
                    font-size: 18px;
                }

                #kbola-advertising-audience-page .audience-chart {
                    height: 320px;
                    padding: 16px;
                }

                #kbola-advertising-audience-page table th {
                    border-top: 0;
                    color: #64748b;
                    font-size: 12px;
                    text-transform: uppercase;
                    white-space: nowrap;
                }

                @media (max-width: 991.98px) {
                    #kbola-advertising-audience-page .audience-filter-row {
                        grid-template-columns: 1fr;
                    }

                    #kbola-advertising-audience-page .audience-filter-actions {
                        justify-content: flex-start;
                    }
                }
            `}</style>
        </div>
    );
}

export default Audience;
